import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GamePhase } from '../types/game';

interface WebGLBackgroundProps {
  gameState: GamePhase;
}

const WebGLBackground: React.FC<WebGLBackgroundProps> = ({ gameState }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    clock: THREE.Clock;
    neuralNetwork: { nodes: THREE.Mesh[]; connections: THREE.Line[] } | null;
    particleSystem: THREE.Points | null;
    backgroundGeometry: THREE.Mesh | null;
    floatingElements: THREE.Mesh[];
    mousePos: { x: number; y: number };
    pulseIntensity: number;
    colorShift: number;
    animationId: number | null;
  } | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Initialize scene
    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x1a1a2e, 50, 200);

    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 50;

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);

    const clock = new THREE.Clock();

    // Create neural network
    const neuralNetwork = createNeuralNetwork(scene);

    // Create particle system
    const particleSystem = createParticleSystem(scene);

    // Create background geometry
    const backgroundGeometry = createBackgroundGeometry(scene);

    // Create floating elements
    const floatingElements = createFloatingElements(scene);

    // Setup lighting
    setupLighting(scene);

    sceneRef.current = {
      scene,
      camera,
      renderer,
      clock,
      neuralNetwork,
      particleSystem,
      backgroundGeometry,
      floatingElements,
      mousePos: { x: 0, y: 0 },
      pulseIntensity: 0.2,
      colorShift: 0,
      animationId: null
    };

    // Mouse move handler
    const handleMouseMove = (event: MouseEvent) => {
      if (sceneRef.current) {
        sceneRef.current.mousePos.x = (event.clientX / window.innerWidth) * 2 - 1;
        sceneRef.current.mousePos.y = -(event.clientY / window.innerHeight) * 2 + 1;
      }
    };

    // Window resize handler
    const handleResize = () => {
      if (sceneRef.current) {
        sceneRef.current.camera.aspect = window.innerWidth / window.innerHeight;
        sceneRef.current.camera.updateProjectionMatrix();
        sceneRef.current.renderer.setSize(window.innerWidth, window.innerHeight);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);

    // Animation loop
    const animate = () => {
      if (!sceneRef.current) return;

      const { scene, camera, renderer, clock, neuralNetwork, particleSystem, backgroundGeometry, floatingElements, mousePos, pulseIntensity, colorShift } = sceneRef.current;
      const time = clock.getElapsedTime();

      // Update neural network pulsing
      if (neuralNetwork) {
        neuralNetwork.nodes.forEach(node => {
          const pulse = Math.sin(time * (node.userData as any).pulseSpeed + (node.userData as any).pulseOffset) * 0.1 + 1;
          node.scale.setScalar(pulse);
        });
      }

      // Update particle system
      if (particleSystem) {
        const positions = particleSystem.geometry.attributes.position.array as Float32Array;
        const velocities = particleSystem.geometry.attributes.velocity.array as Float32Array;

        for (let i = 0; i < positions.length; i += 3) {
          positions[i] += velocities[i];
          positions[i + 1] += velocities[i + 1];
          positions[i + 2] += velocities[i + 2];

          if (Math.abs(positions[i]) > 100) velocities[i] *= -1;
          if (Math.abs(positions[i + 1]) > 100) velocities[i + 1] *= -1;
          if (Math.abs(positions[i + 2]) > 50) velocities[i + 2] *= -1;
        }
        particleSystem.geometry.attributes.position.needsUpdate = true;
      }

      // Update background geometry
      if (backgroundGeometry) {
        const material = backgroundGeometry.material as THREE.ShaderMaterial;
        material.uniforms.time.value = time;
        material.uniforms.colorShift.value = colorShift;
        material.uniforms.pulseIntensity.value = pulseIntensity;
        material.uniforms.mousePos.value.set(mousePos.x, mousePos.y);
      }

      // Update floating elements
      floatingElements.forEach(element => {
        element.rotation.x += (element.userData as any).rotationSpeed.x;
        element.rotation.y += (element.userData as any).rotationSpeed.y;
        element.rotation.z += (element.userData as any).rotationSpeed.z;

        const floatY = Math.sin(time * (element.userData as any).floatSpeed + (element.userData as any).floatOffset) * 3;
        element.position.y += floatY * 0.01;
      });

      // Camera subtle movement
      camera.position.x = Math.sin(time * 0.1) * 0.5;
      camera.position.y = Math.cos(time * 0.15) * 0.25;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      sceneRef.current.animationId = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      if (sceneRef.current?.animationId) {
        cancelAnimationFrame(sceneRef.current.animationId);
      }
      renderer.dispose();
    };
  }, []);

  // Update game state effect
  useEffect(() => {
    if (!sceneRef.current) return;

    switch (gameState) {
      case 'LOBBY':
        sceneRef.current.pulseIntensity = 0.3;
        sceneRef.current.colorShift = 0.1;
        break;
      case 'QUESTION':
        sceneRef.current.pulseIntensity = 0.4;
        sceneRef.current.colorShift = 0.2;
        break;
      case 'REVEAL':
        sceneRef.current.pulseIntensity = 0.5;
        sceneRef.current.colorShift = 0.3;
        break;
      case 'PODIUM':
        sceneRef.current.pulseIntensity = 0.8;
        sceneRef.current.colorShift = 0.5;
        break;
      default:
        sceneRef.current.pulseIntensity = 0.2;
        sceneRef.current.colorShift = 0;
    }
  }, [gameState]);

  return <canvas ref={canvasRef} id="webgl-canvas" className="fixed inset-0 w-full h-full z-0" />;
};

// Helper functions
function createNeuralNetwork(scene: THREE.Scene) {
  const nodeGeometry = new THREE.SphereGeometry(0.3, 8, 8);
  const nodes: THREE.Mesh[] = [];
  const connections: THREE.Line[] = [];

  for (let i = 0; i < 20; i++) {
    const nodeMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color().setHSL(0.6 + Math.random() * 0.3, 0.4, 0.5),
      transparent: true,
      opacity: 0.3
    });

    const node = new THREE.Mesh(nodeGeometry, nodeMaterial);
    node.position.set(
      (Math.random() - 0.5) * 100,
      (Math.random() - 0.5) * 100,
      (Math.random() - 0.5) * 60
    );

    node.userData = {
      originalScale: 1,
      pulseSpeed: 0.5 + Math.random() * 1.5,
      pulseOffset: Math.random() * Math.PI * 2
    };

    nodes.push(node);
    scene.add(node);
  }

  const connectionMaterial = new THREE.LineBasicMaterial({
    color: 0x4a90e2,
    transparent: true,
    opacity: 0.1
  });

  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const distance = nodes[i].position.distanceTo(nodes[j].position);
      if (distance < 25 && Math.random() > 0.7) {
        const geometry = new THREE.BufferGeometry().setFromPoints([
          nodes[i].position,
          nodes[j].position
        ]);
        const line = new THREE.Line(geometry, connectionMaterial);
        connections.push(line);
        scene.add(line);
      }
    }
  }

  return { nodes, connections };
}

function createParticleSystem(scene: THREE.Scene) {
  const particleCount = 500;
  const particles = new THREE.BufferGeometry();
  const positions = new Float32Array(particleCount * 3);
  const colors = new Float32Array(particleCount * 3);
  const velocities = new Float32Array(particleCount * 3);

  for (let i = 0; i < particleCount; i++) {
    const i3 = i * 3;

    positions[i3] = (Math.random() - 0.5) * 200;
    positions[i3 + 1] = (Math.random() - 0.5) * 200;
    positions[i3 + 2] = (Math.random() - 0.5) * 100;

    const colorChoices = [
      { r: 0.54, g: 0.36, b: 0.96 },
      { r: 0.93, g: 0.29, b: 0.6 },
      { r: 0.98, g: 0.45, b: 0.09 },
      { r: 0.23, g: 0.51, b: 0.96 },
      { r: 0.06, g: 0.73, b: 0.51 }
    ];
    const color = colorChoices[Math.floor(Math.random() * colorChoices.length)];
    colors[i3] = color.r;
    colors[i3 + 1] = color.g;
    colors[i3 + 2] = color.b;

    velocities[i3] = (Math.random() - 0.5) * 0.02;
    velocities[i3 + 1] = (Math.random() - 0.5) * 0.02;
    velocities[i3 + 2] = (Math.random() - 0.5) * 0.02;
  }

  particles.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  particles.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  particles.setAttribute('velocity', new THREE.BufferAttribute(velocities, 3));

  const particleMaterial = new THREE.PointsMaterial({
    size: 1.5,
    vertexColors: true,
    transparent: true,
    opacity: 0.3,
    blending: THREE.AdditiveBlending
  });

  const particleSystem = new THREE.Points(particles, particleMaterial);
  scene.add(particleSystem);

  return particleSystem;
}

function createBackgroundGeometry(scene: THREE.Scene) {
  const geometry = new THREE.PlaneGeometry(300, 300, 50, 50);

  const material = new THREE.ShaderMaterial({
    uniforms: {
      time: { value: 0 },
      colorShift: { value: 0 },
      pulseIntensity: { value: 0.5 },
      mousePos: { value: new THREE.Vector2(0, 0) }
    },
    vertexShader: `
      uniform float time;
      uniform float pulseIntensity;
      uniform vec2 mousePos;
      varying vec2 vUv;
      varying vec3 vPosition;

      void main() {
        vUv = uv;
        vPosition = position;

        vec3 pos = position;
        float wave = sin(pos.x * 0.05 + time) * cos(pos.y * 0.05 + time) * pulseIntensity;
        pos.z += wave * 5.0;

        float mouseDistance = distance(pos.xy, mousePos * 100.0);
        pos.z += sin(mouseDistance * 0.1 - time * 2.0) * pulseIntensity * 3.0;

        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }
    `,
    fragmentShader: `
      uniform float time;
      uniform float colorShift;
      uniform float pulseIntensity;
      varying vec2 vUv;
      varying vec3 vPosition;

      void main() {
        vec2 center = vec2(0.5, 0.5);
        float dist = distance(vUv, center);

        vec3 color1 = vec3(0.54, 0.36, 0.96);
        vec3 color2 = vec3(0.93, 0.29, 0.6);
        vec3 color3 = vec3(0.98, 0.45, 0.09);

        float mixFactor1 = sin(vPosition.x * 0.01 + time + colorShift) * 0.5 + 0.5;
        float mixFactor2 = cos(vPosition.y * 0.01 + time * 0.7) * 0.5 + 0.5;

        vec3 finalColor = mix(
          mix(color1, color2, mixFactor1),
          color3,
          mixFactor2
        );

        finalColor *= (1.0 + pulseIntensity * sin(time * 3.0) * 0.1);

        float alpha = (1.0 - dist * 1.5) * 0.3;

        gl_FragColor = vec4(finalColor, alpha);
      }
    `,
    transparent: true,
    side: THREE.DoubleSide
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.z = -30;
  scene.add(mesh);

  return mesh;
}

function createFloatingElements(scene: THREE.Scene) {
  const shapes = [
    { geometry: new THREE.TetrahedronGeometry(2), color: 0x8B5CF6 },
    { geometry: new THREE.OctahedronGeometry(1.5), color: 0xEC4899 },
    { geometry: new THREE.IcosahedronGeometry(1.8), color: 0xF97316 },
    { geometry: new THREE.DodecahedronGeometry(1.2), color: 0x3B82F6 }
  ];

  const elements: THREE.Mesh[] = [];

  for (let i = 0; i < 8; i++) {
    const shape = shapes[Math.floor(Math.random() * shapes.length)];
    const material = new THREE.MeshPhongMaterial({
      color: shape.color,
      transparent: true,
      opacity: 0.3,
      shininess: 50
    });

    const element = new THREE.Mesh(shape.geometry, material);
    element.position.set(
      (Math.random() - 0.5) * 120,
      (Math.random() - 0.5) * 120,
      (Math.random() - 0.5) * 80
    );

    element.userData = {
      rotationSpeed: {
        x: (Math.random() - 0.5) * 0.02,
        y: (Math.random() - 0.5) * 0.02,
        z: (Math.random() - 0.5) * 0.02
      },
      floatSpeed: 0.3 + Math.random() * 0.7,
      floatOffset: Math.random() * Math.PI * 2
    };

    elements.push(element);
    scene.add(element);
  }

  return elements;
}

function setupLighting(scene: THREE.Scene) {
  const ambientLight = new THREE.AmbientLight(0x404040, 0.3);
  scene.add(ambientLight);

  const colors = [0x8B5CF6, 0xEC4899, 0xF97316, 0x3B82F6, 0x10B981];
  for (let i = 0; i < 3; i++) {
    const light = new THREE.PointLight(colors[i], 0.5, 80);
    light.position.set(
      (Math.random() - 0.5) * 100,
      (Math.random() - 0.5) * 100,
      (Math.random() - 0.5) * 50
    );
    scene.add(light);
  }
}

export default WebGLBackground;